import React from 'react';
import { Rocket, Target, ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { TemplateCard } from './TemplateCard';
import { KPICard } from './KPICard';

export const StepKPISelection = ({
  templates,
  kpis,
  loading,
  loadingTemplate,
  showTemplates,
  setShowTemplates,
  selectedKPI,
  handleKPISelect,
  handleTemplateSelect,
  templatesRef,
  constraintStage
}) => {
  return (
    <div>
      {/* Quick-Start Templates Section */}
      {templates.length > 0 && showTemplates && (
        <div className="mb-8" ref={templatesRef}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Rocket className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-gray-900">Quick-Start Templates</h2>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                RECOMMENDED
              </span>
            </div>
            <button
              onClick={() => setShowTemplates(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Show all KPIs instead
            </button>
          </div>
          <p className="text-gray-600 mb-4">
            Choose a common business scenario to get started quickly with pre-configured interventions.
          </p>
          
          {loadingTemplate ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
              <span className="ml-3 text-gray-600">Loading template...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={handleTemplateSelect}
                  loading={loadingTemplate}
                  constraintStage={constraintStage}
                />
              ))}
            </div>
          )}
          
          {/* Redesigned "Not seeing what you want?" section */}
          <div className="mt-8 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm border">
                  <Target className="text-slate-600" size={24} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Not seeing what you need?</p>
                  <p className="text-sm text-gray-500">Select from all available KPIs to build a custom solution</p>
                </div>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                data-testid="select-kpi-manually-btn"
              >
                Select KPIs Manually
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* KPI Selection Section */}
      {(!showTemplates || templates.length === 0) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">What business issue are you trying to solve?</h2>
              <p className="text-gray-600 mt-1">Select the KPI that needs improvement in your organization.</p>
            </div>
            {templates.length > 0 && (
              <button
                onClick={() => setShowTemplates(true)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
              >
                <Sparkles size={16} />
                Show Quick-Start Templates
              </button>
            )}
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kpis.map(kpi => (
                <KPICard
                  key={kpi.id}
                  kpi={kpi}
                  selected={selectedKPI?.id === kpi.id}
                  onClick={() => handleKPISelect(kpi)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
